/**
 * The two hooks every page is written against: read once, and write then read
 * again.
 *
 * website-enterprise repeats both by hand on each page, which is fine for six
 * pages and stopped being fine at sixteen — the third copy of `act` had already
 * lost its `setOk(null)` and left the previous success sitting above a failure.
 * The behaviour is the same, in one place.
 *
 * ── Why every write refetches ───────────────────────────────────────────────
 * The same rule Access.tsx states: a write changes more than the field it was
 * sent for. Publishing a course moves it between tag rows and changes the
 * dashboard's "6 live". Assigning changes a learner count on a row nobody
 * clicked. Patching a local copy of the response would keep the page consistent
 * with the request and not with the server.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { errorText } from './api';

export interface Loaded<T> {
  data: T | null;
  error: string | null;
  /** True until the first answer, success or failure, has arrived. */
  loading: boolean;
  reload: () => Promise<void>;
  setData: (next: T | null) => void;
}

/**
 * One GET, again whenever `key` changes.
 *
 * `key` is how a filtered page re-reads: the report passes its team and period
 * in it, so changing a filter is a new load rather than a second hook. The
 * loader itself is held in a ref, so a caller does not have to memoise it.
 */
export function useLoad<T>(loader: () => Promise<T>, fallback: string, key = ''): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const ref = useRef(loader);
  ref.current = loader;

  // Only the newest load may write state. Without this a slow first request
  // lands after a fast second one and the page shows the filter it no longer
  // has selected.
  const generation = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++generation.current;
    setLoading(true);
    try {
      const next = await ref.current();
      if (generation.current !== mine) return;
      setData(next);
      setError(null);
    } catch (err) {
      if (generation.current !== mine) return;
      setError(errorText(err, fallback));
    }
    if (generation.current === mine) setLoading(false);
  }, [fallback]);

  useEffect(() => {
    void reload();
  }, [reload, key]);

  return { data, error, loading, reload, setData };
}

export interface Actions {
  busy: boolean;
  error: string | null;
  ok: string | null;
  /** Run a write, report it, and read the page again. */
  act: (fn: () => Promise<unknown>, done: string) => Promise<void>;
  setError: (message: string | null) => void;
  setOk: (message: string | null) => void;
}

export function useActions(reload: () => Promise<void>): Actions {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const act = useCallback(
    async (fn: () => Promise<unknown>, done: string) => {
      setBusy(true);
      setError(null);
      setOk(null);
      try {
        await fn();
        setOk(done);
        await reload();
      } catch (err) {
        setError(errorText(err, 'That did not work.'));
      }
      setBusy(false);
    },
    [reload]
  );

  return { busy, error, ok, act, setError, setOk };
}
